import { useCallback, useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';

interface HarnessOrg {
  identifier: string;
  name: string;
}

export const HarnessOrgPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const [orgs, setOrgs] = useState<HarnessOrg[]>([]);
  const [selected, setSelected] = useState<string>(formData ?? '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const backendBaseUrl = configApi.getString('backend.baseUrl');

  useEffect(() => {
    const fetchAllOrgs = async () => {
      let page = 0;
      let allItems: HarnessOrg[] = [];
      let hasMore = true;

      while (hasMore) {
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/proxy/harness-orgs?accountIdentifier=nYY7inrwTrqqa3r1a_-krg&pageSize=100&pageIndex=${page}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items: HarnessOrg[] = (data?.data?.content ?? []).map(
          (item: { organization: HarnessOrg }) => item.organization,
        );
        allItems = allItems.concat(items);
        const totalPages = data?.data?.totalPages ?? 1;
        page += 1;
        hasMore = page < totalPages;
      }

      return allItems;
    };

    fetchAllOrgs()
      .then(items => {
        setOrgs(items.sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      })
      .catch(err => {
        setError(String(err.message ?? err));
        setLoading(false);
      });
  }, [fetchApi, backendBaseUrl]);

  const handleChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const val = e.target.value;
      setSelected(val);
      onChange(val);
    },
    [onChange],
  );

  return (
    <FormControl
      fullWidth
      error={!!rawErrors?.length || !!error}
      required={required}
    >
      <InputLabel shrink>{schema.title ?? 'Organisation'}</InputLabel>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Select value={selected} onChange={handleChange} displayEmpty>
          <MenuItem value="" disabled>
            Select an organisation
          </MenuItem>
          {orgs.map(o => (
            <MenuItem key={o.identifier} value={o.identifier}>
              {o.name}
            </MenuItem>
          ))}
        </Select>
      )}
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
